import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { ChevronDown, ChevronRight, Check, X } from "lucide-react";
import {
  fetchExpenseRegister,
  patchExpenseRegisterItem,
} from "../graphClient";
import { formatCostCents } from "../lib/format";
import SearchInput from "./SearchInput";

const STATUS_VALUES = ["Active", "Migrating", "Cancelled", "CHECK"];

function statusBadgeClass(status) {
  switch (status) {
    case "Active":
      return "bg-emerald-100 text-emerald-800";
    case "Migrating":
      return "bg-amber-100 text-amber-800";
    case "Cancelled":
      return "bg-slate-200 text-slate-500 line-through";
    case "CHECK":
      return "bg-amber-200 text-amber-900 font-bold";
    default:
      return "bg-slate-100 text-slate-600";
  }
}

function statusRowTint(status) {
  switch (status) {
    case "Cancelled":
      return "bg-slate-50/60 text-slate-400";
    case "CHECK":
      return "bg-amber-50/60";
    case "Migrating":
      return "bg-amber-50/30";
    default:
      return "";
  }
}

// ─── Inline-edit infrastructure ──────────────────────────────────────────

function useInlinePatch() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const [state, setState] = useState({ phase: "idle" });
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const save = useCallback(
    async (itemId, fields) => {
      clearTimeout(timer.current);
      setState({ phase: "saving" });
      try {
        await patchExpenseRegisterItem(instance, account, itemId, fields);
        setState({ phase: "saved" });
        timer.current = setTimeout(
          () => setState({ phase: "idle" }),
          1500
        );
        return { ok: true };
      } catch (err) {
        setState({ phase: "error", error: err.message });
        timer.current = setTimeout(
          () => setState({ phase: "idle" }),
          3500
        );
        return { ok: false, error: err.message };
      }
    },
    [instance, account]
  );

  return { state, save };
}

function StateIndicator({ phase, error }) {
  if (phase === "saving") {
    return (
      <span className="text-[10px] text-slate-400 ml-1 select-none">
        saving…
      </span>
    );
  }
  if (phase === "saved") {
    return (
      <Check
        size={12}
        strokeWidth={3}
        className="inline-block text-emerald-600 ml-1"
        aria-label="Saved"
      />
    );
  }
  if (phase === "error") {
    return (
      <span
        title={error}
        className="inline-flex items-center text-[10px] text-red-600 ml-1"
      >
        <X size={12} strokeWidth={3} /> failed
      </span>
    );
  }
  return null;
}

function StatusEditor({ item, updateItem }) {
  const [open, setOpen] = useState(false);
  const { state, save } = useInlinePatch();
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  async function choose(newStatus) {
    setOpen(false);
    if (newStatus === item.status) return;
    const prev = item.status;
    updateItem(item.id, { status: newStatus }); // optimistic
    const { ok } = await save(item.id, { Status: newStatus });
    if (!ok) updateItem(item.id, { status: prev }); // revert
  }

  const label = item.status || "Set status";

  return (
    <span
      ref={rootRef}
      className="relative inline-flex items-center align-middle"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`text-xs px-2 py-0.5 rounded transition-shadow ${statusBadgeClass(
          item.status
        )} ${item.status ? "" : "border border-dashed border-slate-300 text-slate-500"} hover:ring-2 hover:ring-[#1B3A5C]/30 focus:outline-none focus:ring-2 focus:ring-[#1B3A5C]/40`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {label}
      </button>
      <StateIndicator phase={state.phase} error={state.error} />
      {open && (
        <div
          role="listbox"
          className="absolute z-20 top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-1 min-w-[130px]"
        >
          {STATUS_VALUES.map((s) => {
            const selected = s === item.status;
            return (
              <button
                key={s}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => choose(s)}
                className="w-full flex items-center gap-2 text-left text-xs px-2 py-1.5 rounded hover:bg-slate-100"
              >
                <span
                  className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${statusBadgeClass(
                    s
                  )}`}
                >
                  {s}
                </span>
                {selected && (
                  <Check
                    size={12}
                    strokeWidth={3}
                    className="ml-auto text-slate-400"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}

// Generic tap-to-edit text field. `itemField` is the local key on the item
// (e.g. "notes"); `graphField` is the SharePoint internal name in the PATCH
// payload (e.g. "Notes"). Single-line edits use an <input>; multi-line use
// a <textarea>. Saves on blur or after 2s of no typing.
function InlineTextEditor({
  item,
  updateItem,
  itemField,
  graphField,
  placeholder = "+",
  multiline = false,
  rows = 2,
  displayClass = "text-xs text-slate-500",
  emptyClass = "text-slate-300 italic",
  inputMinWidth = 140,
}) {
  const currentValue = item[itemField] || "";
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentValue);
  const { state, save } = useInlinePatch();
  const debounceRef = useRef(null);
  const committedRef = useRef(currentValue);

  useEffect(() => {
    if (!editing) {
      setValue(item[itemField] || "");
      committedRef.current = item[itemField] || "";
    }
  }, [item, itemField, editing]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const commit = useCallback(
    async (next) => {
      clearTimeout(debounceRef.current);
      const prev = committedRef.current;
      if (next === prev) return;
      committedRef.current = next;
      updateItem(item.id, { [itemField]: next }); // optimistic
      const { ok } = await save(item.id, { [graphField]: next });
      if (!ok) {
        committedRef.current = prev;
        updateItem(item.id, { [itemField]: prev });
        setValue(prev);
      }
    },
    [item.id, itemField, graphField, save, updateItem]
  );

  function handleChange(e) {
    const next = e.target.value;
    setValue(next);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => commit(next), 2000);
  }

  function handleBlur() {
    clearTimeout(debounceRef.current);
    commit(value);
    setEditing(false);
  }

  function handleKeyDown(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      clearTimeout(debounceRef.current);
      setValue(committedRef.current);
      setEditing(false);
    }
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      e.target.blur();
    }
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-start gap-1 text-left max-w-full hover:bg-slate-100 px-1 py-0.5 rounded transition-colors"
      >
        <span
          className={`${displayClass} break-words ${
            multiline ? "whitespace-pre-wrap line-clamp-2" : "truncate"
          }`}
        >
          {currentValue ? (
            currentValue
          ) : (
            <span className={emptyClass}>{placeholder}</span>
          )}
        </span>
        <StateIndicator phase={state.phase} error={state.error} />
      </button>
    );
  }

  const baseInputClass =
    "text-xs border border-slate-300 rounded px-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-[#1B3A5C]/30";

  return (
    <span
      className="inline-flex flex-col items-stretch gap-0.5 w-full"
      style={{ minWidth: inputMinWidth }}
    >
      {multiline ? (
        <textarea
          autoFocus
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          rows={rows}
          className={`${baseInputClass} resize-y min-h-[44px]`}
        />
      ) : (
        <input
          autoFocus
          type="text"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={baseInputClass}
        />
      )}
      <span className="flex items-center justify-between text-[10px] text-slate-400 px-0.5">
        <span>
          {multiline
            ? "Auto-saves on blur · Esc to cancel"
            : "Auto-saves · Enter or blur to commit · Esc to cancel"}
        </span>
        <StateIndicator phase={state.phase} error={state.error} />
      </span>
    </span>
  );
}

function NotesEditor({ item, updateItem }) {
  return (
    <InlineTextEditor
      item={item}
      updateItem={updateItem}
      itemField="notes"
      graphField="Notes"
      placeholder="+ note"
      multiline
      rows={2}
    />
  );
}

function HistoricalAccountEditor({ item, updateItem }) {
  return (
    <InlineTextEditor
      item={item}
      updateItem={updateItem}
      itemField="historicalAccount"
      graphField="HistoricalAccount"
      placeholder="+ historical account"
      multiline={false}
      displayClass="text-xs text-slate-600"
      inputMinWidth={150}
    />
  );
}

function sumMonthly(items, opts = {}) {
  const excludeCancelled = opts.excludeCancelled ?? true;
  return items.reduce((acc, it) => {
    if (excludeCancelled && it.status === "Cancelled") return acc;
    return acc + (typeof it.monthlyEquivalent === "number" ? it.monthlyEquivalent : 0);
  }, 0);
}

function groupBy(items, keyFn) {
  const map = new Map();
  for (const it of items) {
    const k = keyFn(it) || "—";
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(it);
  }
  return Array.from(map.entries()).map(([key, list]) => ({ key, items: list }));
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? "bg-[#1B3A5C] border-[#1B3A5C] text-white"
          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
      }`}
    >
      {children}
    </button>
  );
}

function ViewToggle({ mode, setMode }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200/70 p-1 inline-flex gap-1">
      <button
        onClick={() => setMode("category")}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          mode === "category"
            ? "bg-[#1B3A5C] text-white"
            : "text-slate-600 hover:bg-slate-100"
        }`}
      >
        By category
      </button>
      <button
        onClick={() => setMode("account")}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          mode === "account"
            ? "bg-[#1B3A5C] text-white"
            : "text-slate-600 hover:bg-slate-100"
        }`}
      >
        By account
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Category (CD) view
// ─────────────────────────────────────────────────────────────────────────

function CategoryItemCardMobile({ item, updateItem }) {
  return (
    <div
      className={`rounded-xl border border-slate-200/70 p-3 ${statusRowTint(
        item.status
      )}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-slate-900 text-sm truncate">
            {item.expenseName}
          </p>
          <p className="text-xs text-slate-500 mt-0.5 truncate">
            {item.entity || "—"} · {item.currentAccount || "—"}
          </p>
        </div>
        <StatusEditor item={item} updateItem={updateItem} />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
        <div className="flex justify-between">
          <span>Amount</span>
          <span className="font-medium text-slate-900 tabular-nums">
            {formatCostCents(item.amount)}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Freq</span>
          <span className="font-medium text-slate-900">
            {item.frequency || "—"}
          </span>
        </div>
        <div className="flex justify-between col-span-2">
          <span>Monthly</span>
          <span className="font-semibold text-slate-900 tabular-nums">
            {formatCostCents(item.monthlyEquivalent)}
          </span>
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-slate-200 flex items-baseline justify-between gap-2 text-xs text-slate-600">
        <span className="shrink-0">Historical</span>
        <span className="text-right min-w-0 flex-1">
          <HistoricalAccountEditor item={item} updateItem={updateItem} />
        </span>
      </div>
      <div className="mt-1">
        <NotesEditor item={item} updateItem={updateItem} />
      </div>
    </div>
  );
}

function CategorySection({ category, items, updateItem }) {
  const [open, setOpen] = useState(false);
  const subtotal = sumMonthly(items);
  const Caret = open ? ChevronDown : ChevronRight;
  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Caret size={16} className="text-slate-400 shrink-0" />
          <h3 className="font-semibold text-slate-900 text-sm truncate">
            {category}
          </h3>
          <span className="text-xs text-slate-400 shrink-0">
            ({items.length})
          </span>
        </div>
        <span className="text-sm font-semibold text-[#1B3A5C] tabular-nums shrink-0">
          {formatCostCents(subtotal)}/mo
        </span>
      </button>

      {open && (
        <>
          {/* Mobile cards */}
          <div className="sm:hidden px-3 pb-3 space-y-2">
            {items.map((item) => (
              <CategoryItemCardMobile
                key={item.id}
                item={item}
                updateItem={updateItem}
              />
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto border-t border-slate-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-[0.14em] bg-slate-50/60">
                  <th className="py-2.5 px-4">Expense</th>
                  <th className="py-2.5 px-3">Entity</th>
                  <th className="py-2.5 px-3 text-right">Amount</th>
                  <th className="py-2.5 px-3">Freq</th>
                  <th className="py-2.5 px-3 text-right">Monthly</th>
                  <th className="py-2.5 px-3">Historical</th>
                  <th className="py-2.5 px-3">Current</th>
                  <th className="py-2.5 px-3 text-right">FY24</th>
                  <th className="py-2.5 px-3 text-right">FY25</th>
                  <th className="py-2.5 px-3 text-right">FY26</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 pr-4">Notes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={`border-t border-slate-100 ${statusRowTint(
                      item.status
                    )}`}
                  >
                    <td className="py-2.5 px-4 font-medium text-slate-900">
                      {item.expenseName}
                    </td>
                    <td className="py-2.5 px-3 text-slate-600">
                      {item.entity || "—"}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums">
                      {formatCostCents(item.amount)}
                    </td>
                    <td className="py-2.5 px-3 text-slate-600">
                      {item.frequency || "—"}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums font-medium">
                      {formatCostCents(item.monthlyEquivalent)}
                    </td>
                    <td className="py-2.5 px-3 align-top">
                      <HistoricalAccountEditor
                        item={item}
                        updateItem={updateItem}
                      />
                    </td>
                    <td className="py-2.5 px-3 text-slate-600">
                      {item.currentAccount || "—"}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-slate-500">
                      {formatCostCents(item.fy24)}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-slate-500">
                      {formatCostCents(item.fy25)}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-slate-500">
                      {formatCostCents(item.fy26)}
                    </td>
                    <td className="py-2.5 px-3">
                      <StatusEditor item={item} updateItem={updateItem} />
                    </td>
                    <td className="py-2.5 pr-4 align-top max-w-[220px]">
                      <NotesEditor item={item} updateItem={updateItem} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function CategoryView({ items, updateItem }) {
  const [statusFilter, setStatusFilter] = useState(new Set()); // empty = all
  const [entityFilter, setEntityFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const entities = useMemo(
    () =>
      Array.from(new Set(items.map((i) => i.entity).filter(Boolean))).sort(),
    [items]
  );
  const categories = useMemo(
    () =>
      Array.from(new Set(items.map((i) => i.category).filter(Boolean))).sort(),
    [items]
  );

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (statusFilter.size > 0 && !statusFilter.has(it.status)) return false;
      if (entityFilter && it.entity !== entityFilter) return false;
      if (categoryFilter && it.category !== categoryFilter) return false;
      return true;
    });
  }, [items, statusFilter, entityFilter, categoryFilter]);

  const grouped = useMemo(() => {
    const groups = groupBy(filtered, (i) => i.category);
    for (const g of groups) {
      g.items.sort((a, b) =>
        (a.expenseName || "").localeCompare(b.expenseName || "", undefined, {
          sensitivity: "base",
        })
      );
    }
    groups.sort((a, b) => sumMonthly(b.items) - sumMonthly(a.items));
    return groups;
  }, [filtered]);

  const totalMonthly = sumMonthly(filtered);
  const totalAnnual = totalMonthly * 12;
  const countsByStatus = useMemo(() => {
    const c = {};
    for (const s of STATUS_VALUES) c[s] = 0;
    for (const it of filtered) {
      if (c[it.status] !== undefined) c[it.status]++;
    }
    return c;
  }, [filtered]);

  function toggleStatus(s) {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Monthly total
            </p>
            <p className="text-lg font-semibold text-slate-900 tabular-nums mt-0.5">
              {formatCostCents(totalMonthly)}
            </p>
            <p className="text-[10px] text-slate-400">excl. cancelled</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Annual total
            </p>
            <p className="text-lg font-semibold text-slate-900 tabular-nums mt-0.5">
              {formatCostCents(totalAnnual)}
            </p>
          </div>
          <div className="col-span-2 sm:col-span-2">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              By status
            </p>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {STATUS_VALUES.map((s) => (
                <span
                  key={s}
                  className={`text-xs px-2 py-0.5 rounded ${statusBadgeClass(s)}`}
                >
                  {s} · {countsByStatus[s]}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200/70 p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mr-1">
            Status
          </span>
          {STATUS_VALUES.map((s) => (
            <FilterChip
              key={s}
              active={statusFilter.has(s)}
              onClick={() => toggleStatus(s)}
            >
              {s}
            </FilterChip>
          ))}
          {statusFilter.size > 0 && (
            <button
              onClick={() => setStatusFilter(new Set())}
              className="text-xs text-slate-500 hover:text-slate-700 ml-1"
            >
              clear
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Entity
          </label>
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white"
          >
            <option value="">All</option>
            {entities.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold ml-2">
            Category
          </label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white"
          >
            <option value="">All</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Groups */}
      {grouped.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-8 text-center text-slate-400 text-sm">
          No expenses match the current filters.
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map((g) => (
            <CategorySection
              key={g.key}
              category={g.key}
              items={g.items}
              updateItem={updateItem}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Account (Leanne) view
// ─────────────────────────────────────────────────────────────────────────

function AccountItemCardMobile({ item, updateItem }) {
  return (
    <div
      className={`rounded-xl border border-slate-200/70 p-3 ${statusRowTint(
        item.status
      )}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-slate-900 text-sm truncate">
            {item.expenseName}
          </p>
          <p className="text-xs text-slate-500 mt-0.5 truncate">
            {item.category || "—"} · {item.frequency || "—"}
          </p>
        </div>
        <StatusEditor item={item} updateItem={updateItem} />
      </div>
      <div className="mt-2 flex items-baseline justify-between">
        <span className="text-xs text-slate-500">
          {formatCostCents(item.amount)} {item.frequency ? `/ ${item.frequency.toLowerCase()}` : ""}
        </span>
        <span className="font-semibold text-slate-900 tabular-nums text-sm">
          {formatCostCents(item.monthlyEquivalent)}/mo
        </span>
      </div>
      <div className="mt-2 pt-2 border-t border-slate-200 flex items-baseline justify-between gap-2 text-xs text-slate-600">
        <span className="shrink-0">Historical</span>
        <span className="text-right min-w-0 flex-1">
          <HistoricalAccountEditor item={item} updateItem={updateItem} />
        </span>
      </div>
      <div className="mt-1">
        <NotesEditor item={item} updateItem={updateItem} />
      </div>
    </div>
  );
}

function AccountSection({ account, items, updateItem }) {
  const [open, setOpen] = useState(false);
  const subtotal = sumMonthly(items);
  const Caret = open ? ChevronDown : ChevronRight;
  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Caret size={16} className="text-slate-400 shrink-0" />
          <h3 className="font-semibold text-slate-900 text-sm truncate">
            {account}
          </h3>
          <span className="text-xs text-slate-400 shrink-0">
            ({items.length})
          </span>
        </div>
        <span className="text-sm font-semibold text-[#1B3A5C] tabular-nums shrink-0">
          {formatCostCents(subtotal)}/mo
        </span>
      </button>

      {open && (
        <>
          <div className="sm:hidden px-3 pb-3 space-y-2">
            {items.map((item) => (
              <AccountItemCardMobile
                key={item.id}
                item={item}
                updateItem={updateItem}
              />
            ))}
          </div>

          <div className="hidden sm:block overflow-x-auto border-t border-slate-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-[0.14em] bg-slate-50/60">
                  <th className="py-2.5 px-4">Expense</th>
                  <th className="py-2.5 px-3">Category</th>
                  <th className="py-2.5 px-3 text-right">Amount</th>
                  <th className="py-2.5 px-3">Freq</th>
                  <th className="py-2.5 px-3 text-right">Monthly</th>
                  <th className="py-2.5 px-3">Historical</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 pr-4">Notes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={`border-t border-slate-100 ${statusRowTint(
                      item.status
                    )}`}
                  >
                    <td className="py-2.5 px-4 font-medium text-slate-900">
                      {item.expenseName}
                    </td>
                    <td className="py-2.5 px-3 text-slate-600">
                      {item.category || "—"}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums">
                      {formatCostCents(item.amount)}
                    </td>
                    <td className="py-2.5 px-3 text-slate-600">
                      {item.frequency || "—"}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums font-medium">
                      {formatCostCents(item.monthlyEquivalent)}
                    </td>
                    <td className="py-2.5 px-3 align-top">
                      <HistoricalAccountEditor
                        item={item}
                        updateItem={updateItem}
                      />
                    </td>
                    <td className="py-2.5 px-3">
                      <StatusEditor item={item} updateItem={updateItem} />
                    </td>
                    <td className="py-2.5 pr-4 align-top max-w-[220px]">
                      <NotesEditor item={item} updateItem={updateItem} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function AccountView({ items, updateItem }) {
  const [statusFilter, setStatusFilter] = useState(new Set());
  const [showCancelled, setShowCancelled] = useState(false);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (!showCancelled && it.status === "Cancelled") return false;
      if (statusFilter.size > 0 && !statusFilter.has(it.status)) return false;
      return true;
    });
  }, [items, statusFilter, showCancelled]);

  const grouped = useMemo(() => {
    const groups = groupBy(filtered, (i) => i.currentAccount);
    for (const g of groups) {
      g.items.sort((a, b) =>
        (a.expenseName || "").localeCompare(b.expenseName || "", undefined, {
          sensitivity: "base",
        })
      );
    }
    groups.sort((a, b) => sumMonthly(b.items) - sumMonthly(a.items));
    return groups;
  }, [filtered]);

  const totalMonthly = sumMonthly(filtered);

  function toggleStatus(s) {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  // Show only non-cancelled filter chips in Leanne's view (simpler).
  const visibleStatuses = STATUS_VALUES.filter(
    (s) => s !== "Cancelled" || showCancelled
  );

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-4">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Total per month
            </p>
            <p className="text-2xl font-semibold text-slate-900 tabular-nums mt-0.5">
              {formatCostCents(totalMonthly)}
            </p>
            <p className="text-[10px] text-slate-400">
              across {grouped.length} {grouped.length === 1 ? "account" : "accounts"}
              {!showCancelled && " · cancelled hidden"}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200/70 p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mr-1">
            Status
          </span>
          {visibleStatuses.map((s) => (
            <FilterChip
              key={s}
              active={statusFilter.has(s)}
              onClick={() => toggleStatus(s)}
            >
              {s}
            </FilterChip>
          ))}
          {statusFilter.size > 0 && (
            <button
              onClick={() => setStatusFilter(new Set())}
              className="text-xs text-slate-500 hover:text-slate-700 ml-1"
            >
              clear
            </button>
          )}
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer pt-1">
          <input
            type="checkbox"
            checked={showCancelled}
            onChange={(e) => setShowCancelled(e.target.checked)}
            className="rounded border-slate-300"
          />
          Show cancelled items
        </label>
      </div>

      {/* Groups */}
      {grouped.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-8 text-center text-slate-400 text-sm">
          No expenses match the current filters.
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map((g) => (
            <AccountSection
              key={g.key}
              account={g.key}
              items={g.items}
              updateItem={updateItem}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Top-level view
// ─────────────────────────────────────────────────────────────────────────

export default function ExpenseRegisterView() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  // Both users default to the account-grouped view (and groups start collapsed
  // so the page loads to a scannable summary, not a wall of rows). Either user
  // can flip to By category via the toggle.
  const [mode, setMode] = useState("account");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchExpenseRegister(instance, account);
      setItems(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateItem = useCallback((id, partial) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...partial } : it))
    );
  }, []);

  // Free-text filter across the columns Leanne uses to find an expense.
  const searchedItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      [it.expenseName, it.category, it.notes, it.frequency].some(
        (v) => v && String(v).toLowerCase().includes(q)
      )
    );
  }, [items, query]);

  const showCount = !loading && !error;
  const total = items.length;
  const matched = searchedItems.length;
  const hasQuery = query.trim().length > 0;

  return (
    <div>
      {/* Sticky header — page title row, search input, and view toggle.
          Stays pinned below the global navy bar (~56px) as the list scrolls. */}
      <div className="sticky top-[68px] z-[5] pt-2 pb-3 bg-slate-50 border-b border-slate-200/70 mb-4">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-xs text-slate-500 tabular-nums">
            {loading
              ? "Loading expenses…"
              : hasQuery
              ? `Showing ${matched} of ${total}`
              : `${total} ${total === 1 ? "expense" : "expenses"}`}
          </p>
          <ViewToggle mode={mode} setMode={setMode} />
        </div>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search by name, category, frequency, notes…"
          ariaLabel="Search expenses"
        />
      </div>

      {loading && (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-12 text-center text-slate-400">
          Loading expenses…
        </div>
      )}

      {error && (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-6 text-center">
          <p className="text-slate-900 text-sm font-medium mb-2">
            Failed to load expenses
          </p>
          <p className="text-slate-500 text-xs mb-4 break-all">{error}</p>
          <button
            onClick={load}
            className="text-sm bg-[#1B3A5C] hover:bg-[#0E243B] text-white px-4 py-2 rounded-xl font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {showCount && hasQuery && matched === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-8 text-center">
          <p className="text-sm text-slate-500">
            No items match &ldquo;
            <span className="font-medium text-slate-800">{query}</span>&rdquo;
          </p>
          <button
            type="button"
            onClick={() => setQuery("")}
            className="mt-3 text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2"
          >
            Clear search
          </button>
        </div>
      )}

      {showCount && (matched > 0 || !hasQuery) && (
        mode === "category" ? (
          <CategoryView items={searchedItems} updateItem={updateItem} />
        ) : (
          <AccountView items={searchedItems} updateItem={updateItem} />
        )
      )}
    </div>
  );
}
